import { Button, Col, Row } from "ant-design-vue";
import lodash from "lodash";
import { defineAsyncComponent } from "vue";
import { setIsLoading as setIsLoadingApp, state as stateApp } from "../../../../app.logic";
import {
    createFieldDatetime,
    createFieldInput,
    createFieldInputNumber,
    createFieldRadioGroup,
    createFieldSelect,
    createFieldTextArea
} from "../../../../components/input/template-builder/template-builder.logic";
import { createColumnBoolean, createColumnDatetime, createColumnInput, createColumnSelect } from "../../../../components/table/c-table.logic";
import i18n from "../../../../i18n";
import store from "../../../../store";
import { conditionFormChecker, putField } from "../../../../utils/form";
import { has, matchMenu } from "../../../../utils/menu-context";
import { addRules, clearRulesStartsWith, email, equalsLength, required, requiredWhen } from "../../../../utils/validate";
import { emailRequired, idCardRequired } from "../../../account/login/register.logic";
import { callDepartment } from "../../master-mgt/department-responsible/department-responsible.logic";
import {
    createFieldCompanyAddress,
    createFieldCompanyAttachment,
    createFieldCompanyName,
    createFieldSomeInfomationView
} from "../company/company.section";
import { createWhereRole as createWhereRoleCompany } from "../user-company/user-company.logic";
import { callTitle } from "../user-company/user-company.section";
import { createField as createFieldCompanyInspection } from "./company-inspection.logic";

const CardBorder = defineAsyncComponent(() => import("../../../../components/card/CardBorder"));
const TemplateBuilder = defineAsyncComponent(() => import("../../../../components/input/template-builder/TemplateBuilder"));
const CompanyProductList = defineAsyncComponent(() => import("./CompanyProductList"));
const CompanyCountryList = defineAsyncComponent(() => import("./CompanyCountryList"));
const CompanyInspectionList = defineAsyncComponent(() => import("./CompanyInspectionList"));
const SamplingProductList = defineAsyncComponent(() => import("../company-doc-reject/SamplingProductList"));
const HealthCertList = defineAsyncComponent(() => import("./HealthCertList"));
const AttachmentList = defineAsyncComponent(() => import("../../attachment/AttachmentList"));

export const callUserCompanyByNameTh = ({ searchQuery }) => {
    const where = [
        { name: "refCompany.isActive", type: "Boolean", operator: "=", value: true },
        { name: "refCompany.refStatus.key", type: "String", operator: "in", value: ["Approve", "Cancel"] }
    ];
    if (searchQuery) {
        where.push({ name: "refCompany.nameTh", type: "String", operator: "like", value: searchQuery });
    }
    where.push(...createWhereRoleCompany({ props: { menuContext: matchMenu("/app/company/companies", stateApp.authInfo.menus) } }));
    return store
        .dispatch("resource/v1/transaction/userCompany/search", {
            criteria: {
                select: ["*", "refCompany.*", "refCompany.refStatus.*"],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "refCompany.nameTh", type: "ASC" }]
            }
        })
        .then(page => page.data);
};
export const callCompanyById = ({ id }) => {
    const where = [{ name: "id", type: "UUID", operator: "=", value: id }];
    return store
        .dispatch("resource/v1/transaction/company/search", {
            criteria: {
                select: ["**", "refFactoryAddrTh.**", "refFactoryAddrEn.**", "refOfficeAddrTh.**", "refOfficeAddrEn.**"],
                page: 0,
                size: 1000000,
                where,
                sort: [],
                children: [
                    { name: "companyProducts", select: ["id", "refProduct.id", "refStatus.id", "refStatus.key"], page: 0, size: 1000000, where: [{ name: "isActive", type: "Boolean", operator: "=", value: true }], sort: [] },
                    { name: "companyCountries", select: ["id", "refCountry.id", "refStatus.id", "refStatus.key"], page: 0, size: 1000000, sort: [] }
                ]
            }
        })
        .then(page => page.data[0]);
};
export const callCloneCompany = ({ data }) => store.dispatch("resource/v1/transaction/companyRequest/cloneCompany", { data });
export const callCompanyRequestType = ({ searchQuery, company, props }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "COMPANY_REQUEST_TYPE", value: searchQuery }).then(items => {
        const { menuContext } = props;
        if (!company) return items;
        if (!company.thNo) {
            const allow = ["1"];
            if (has({ menuContext, key: "forCompany" })) allow.push("9");
            return items.filter(item => allow.includes(item.key));
        }
        if (company.refStatus?.key === "Cancel") {
            const allow = ["2"];
            return items.filter(item => allow.includes(item.key));
        }
        const disAllow = ["1", "2"];
        if (!has({ menuContext, key: "forCompany" })) disAllow.push("9");
        return items.filter(item => !disAllow.includes(item.key));
    });
export const callCompanyRequestStatus = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "COMPANY_REQUEST_STATUS", value: searchQuery });
export const callCompanyRequestStatusForReceive = () =>
    callCompanyRequestStatus({}).then(statuses => statuses.filter(status => ["Re Edit", "Wait Review", "Not Receive"].includes(status.key)));

export const createColumns = ({ props }) => {
    let defaultCompanyDepartment = null;
    if (!has({ menuContext: props?.menuContext, key: "forCompany" })) {
        if (!has({ menuContext: props?.menuContext, key: "canSearchByCompanyDepartment" })) {
            defaultCompanyDepartment =
                store.state.resource.auth.info?.user?.refDepartment?.refParent || store.state.resource.auth.info?.user?.refDepartment;
        }
    }
    const columns = [
        createColumnDatetime({ title: () => i18n.global.t("model.companyRequest.requestDatetime"), dataIndex: "requestDatetime" }),
        createColumnInput({ title: () => i18n.global.t("model.companyRequest.requestNo"), dataIndex: "requestNo", align: "center" }),
        createColumnSelect({
            title: () => i18n.global.t("model.companyRequest.refCompanyRequestType"),
            dataIndex: "refCompanyRequestType.value",
            customRender: ({ record }) => <div>{record.refCompanyRequestType?.value}</div>,
            inputProp: () => ({ list: ({ searchQuery }) => callCompanyRequestType({ searchQuery, props }), optLabel: "value" }),
            search: { name: "refCompanyRequestType.id" }
        }),
        createColumnInput({ title: () => i18n.global.t("model.company.thNo"), dataIndex: "refCompanyTmp.thNo", align: "center" }),
        createColumnInput({ title: () => i18n.global.t("model.company.nameTh"), dataIndex: "refCompanyTmp.nameTh" }),
        createColumnSelect({
            title: () => i18n.global.t("model.company.refDepartment"),
            dataIndex: "refCompanyTmp.refDepartment.nameTh",
            customRender: ({ record }) => <div>{record.refCompanyTmp?.refDepartment?.nameTh}</div>,
            inputProp: () => ({ list: callDepartment, optLabel: "nameTh" }),
            search: {
                name: "refCompanyTmp.refDepartment.id",
                visible: () => has({ menuContext: props?.menuContext, key: "canSearchByCompanyDepartment" }),
                field: { searchValue: defaultCompanyDepartment }
            }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.companyRequest.refStatus"),
            dataIndex: "refStatus.value",
            customRender: ({ record }) => <div>{record.refStatus?.value}</div>,
            inputProp: () => ({ list: callCompanyRequestStatus, optLabel: "value" }),
            align: "center",
            search: { name: "refStatus.id" }
        }),
        createColumnBoolean({ title: () => i18n.global.t("model.companyRequest.isOfficerRequest"), dataIndex: "isOfficerRequest" }),
        createColumnDatetime({ title: () => i18n.global.t("model.updateDate"), dataIndex: "updateDate" }),
        createColumnInput({ title: () => i18n.global.t("model.createBy"), dataIndex: "refCreateBy.fullName" }),
        createColumnDatetime({ title: () => i18n.global.t("model.createDate"), dataIndex: "createDate" })
    ];
    return columns;
};

export const createFieldUser = ({ props, data, key }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return [
        createFieldInputNumber({
            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
            key: `${prefix}idCard`,
            colProp: () => ({ xs: 24, md: 12 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.user.idCard"),
            inputProp: ({ field }) =>
                lodash.merge(
                    {
                        value: model.idCard,
                        "onUpdate:value": value => (model.idCard = value),
                        disabled: viewOnly,
                        comma: false,
                        onBlur: () => useForm.validate(field.key).catch(() => {})
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                )
        }),
        createFieldSelect({
            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
            key: `${prefix}refTitle`,
            colProp: () => ({ xs: 24, md: 12 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.lookup.modelGroup.TITLE"),
            inputProp: ({ field }) =>
                lodash.merge(
                    {
                        value: model.refTitle,
                        "onUpdate:value": value => (model.refTitle = value),
                        disabled: viewOnly,
                        list: callTitle,
                        optKey: "key",
                        optLabel: "value"
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                ),
            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
        }),
        createFieldInput({
            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
            key: `${prefix}firstNameTh`,
            colProp: () => ({ xs: 24, md: 12 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.user.firstNameTh"),
            inputProp: ({ field }) =>
                lodash.merge(
                    {
                        value: model.firstNameTh,
                        "onUpdate:value": value => (model.firstNameTh = value),
                        disabled: viewOnly
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                )
        }),
        createFieldInput({
            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
            key: `${prefix}lastNameTh`,
            colProp: () => ({ xs: 24, md: 12 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.user.lastNameTh"),
            inputProp: ({ field }) =>
                lodash.merge(
                    {
                        value: model.lastNameTh,
                        "onUpdate:value": value => (model.lastNameTh = value),
                        disabled: viewOnly
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                )
        }),
        createFieldInput({
            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
            key: `${prefix}email`,
            colProp: () => ({ xs: 24, md: 12 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.user.email"),
            inputProp: ({ field }) =>
                lodash.merge(
                    {
                        value: model.email,
                        "onUpdate:value": value => (model.email = value),
                        disabled: viewOnly,
                        onBlur: () => useForm.validate(field.key).catch(() => {})
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                )
        }),
        createFieldInputNumber({
            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
            key: `${prefix}phone`,
            colProp: () => ({ xs: 24, md: 12 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.user.phone"),
            inputProp: ({ field }) =>
                lodash.merge(
                    {
                        value: model.phone,
                        "onUpdate:value": value => (model.phone = value),
                        disabled: viewOnly,
                        comma: false
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                )
        }),
        createFieldInputNumber({
            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
            key: `${prefix}mobile`,
            colProp: () => ({ xs: 24, md: 12 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.user.mobile"),
            inputProp: ({ field }) =>
                lodash.merge(
                    {
                        value: model.mobile,
                        "onUpdate:value": value => (model.mobile = value),
                        disabled: viewOnly,
                        comma: false
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                )
        }),
        createFieldInput({
            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
            key: `${prefix}fax`,
            colProp: () => ({ xs: 24, md: 12 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.user.fax"),
            inputProp: ({ field }) =>
                lodash.merge(
                    {
                        value: model.fax,
                        "onUpdate:value": value => (model.fax = value),
                        disabled: viewOnly
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                )
        })
    ];
};

export const createField = ({ props, data, refs, key, isLoading }) => {
    const { viewOnly, menuContext } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    return putField({
        data,
        fields: [
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "CompanyRequest" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    return (
                        <CardBorder title={i18n.global.t("views.company.companyRequest.title")}>
                            <TemplateBuilder
                                rowProp={data.rowProp}
                                useForm={data.useForm}
                                fields={createFieldCompanyRequest({ props, data, key, isLoading })}
                                skeleton={isLoading.value}
                            ></TemplateBuilder>
                            <div class="text-center mt-2">
                                <Button class="btn-primary" onClick={() => onClickOkCompanyRequest({ props, data, key, isLoading })}>
                                    {i18n.global.t("button.ok")}
                                </Button>
                            </div>
                        </CardBorder>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "CompanyRequestHeader" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    let buttonEdit = null;
                    if (!modelRef.id) {
                        //buttonEdit = <Button class="ml-2 my-auto btn-warning" onClick={() => onClickEditCompanyRequest({ props, data, key, isLoading })}>{i18n.global.t('button.edit')}</Button>
                    }
                    return (
                        <div class="d-flex mb-4">
                            <span class="text-primary text-24">{model.refCompanyRequestType?.value}</span>
                            {buttonEdit}
                        </div>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "SomeInfomationView" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const sectionInformation = createFieldSomeInfomationView({
                        props,
                        data,
                        key: "refCompany",
                        isLoading,
                        userCompanyId: model.refCompany?.userCompanies?.[0]?.id
                    });
                    return (
                        <CardBorder title={i18n.global.t("views.company.title")}>
                            <TemplateBuilder
                                rowProp={data.rowProp}
                                useForm={data.useForm}
                                fields={sectionInformation}
                                skeleton={isLoading.value}
                            ></TemplateBuilder>
                        </CardBorder>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "CompanyProduct" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const cardProp = lodash.merge(
                        {
                            title: i18n.global.t("views.company.companyProduct.title")
                        },
                        conditionFormChecker({ props, data, executeKey: "cardProp", params: { sectionKey: "CompanyProduct" } })
                    );
                    const componentProp = lodash.merge(
                        {
                            menuContext,
                            viewOnly: viewOnly,
                            dataProp: data,
                            itemsKey: "refCompanyTmp.companyProducts",
                            visibleCompany: false,
                            selectGetCheckboxPropDisabled: ({ record }) => {
                                return (
                                    model.refCompany.companyProducts.filter(
                                        d => d.refProduct.id === record.id && ["Wait", "Approve"].includes(d.refStatus?.key)
                                    ).length > 0
                                );
                            },
                            ref: e => lodash.set(refs, "companyProductList", e)
                        },
                        conditionFormChecker({ props, data, executeKey: "componentProp", params: { sectionKey: "CompanyProduct" } })
                    );
                    return (
                        <CardBorder {...cardProp}>
                            <CompanyProductList {...componentProp}></CompanyProductList>
                        </CardBorder>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "CompanyCountry" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const cardProp = lodash.merge(
                        {
                            title: i18n.global.t("views.company.companyCountry.title")
                        },
                        conditionFormChecker({ props, data, executeKey: "cardProp", params: { sectionKey: "CompanyCountry" } })
                    );
                    const componentProp = lodash.merge(
                        {
                            menuContext,
                            viewOnly: viewOnly,
                            dataProp: data,
                            itemsKey: "refCompanyTmp.companyCountries",
                            visibleCompany: false,
                            selectGetCheckboxPropDisabled: ({ record }) => {
                                return (
                                    model.refCompany.companyCountries.filter(
                                        d => d.refCountry.id === record.id && ["Wait", "Wait Destination", "Approve"].includes(d.refStatus?.key)
                                    ).length > 0
                                );
                            },
                            ref: e => lodash.set(refs, "companyCountryList", e)
                        },
                        conditionFormChecker({ props, data, executeKey: "componentProp", params: { sectionKey: "CompanyCountry" } })
                    );
                    return (
                        <CardBorder {...cardProp}>
                            <CompanyCountryList {...componentProp}></CompanyCountryList>
                        </CardBorder>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "CompanyAttachment" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const enable = conditionFormChecker({ props, data, executeKey: "enable", params: { sectionKey: "CompanyAttachment" } });
                    const sectionCompanyAttachment = createFieldCompanyAttachment({
                        props: { ...props, viewOnly: !enable },
                        data,
                        key: "refCompanyTmp",
                        isLoading
                    });
                    return (
                        <CardBorder title={i18n.global.t("views.company.register.attachment.title")}>
                            <TemplateBuilder
                                rowProp={data.rowProp}
                                useForm={data.useForm}
                                fields={sectionCompanyAttachment}
                                skeleton={isLoading.value}
                            ></TemplateBuilder>
                        </CardBorder>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "CompanyAddress" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const enable = conditionFormChecker({ props, data, executeKey: "enable", params: { sectionKey: "CompanyAddress" } });
                    const sectionCompanyName = createFieldCompanyName({
                        props: { ...props, viewOnly: !enable },
                        data,
                        key: "refCompanyTmp",
                        isLoading
                    });
                    const sectionCompanyAddress = createFieldCompanyAddress({
                        props: { ...props, viewOnly: !enable },
                        data,
                        key: "refCompanyTmp",
                        isLoading
                    });
                    return (
                        <CardBorder title={i18n.global.t("views.company.address.title")}>
                            <TemplateBuilder
                                rowProp={data.rowProp}
                                useForm={data.useForm}
                                fields={[...sectionCompanyName, ...sectionCompanyAddress]}
                                skeleton={isLoading.value}
                            ></TemplateBuilder>
                        </CardBorder>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "CompanyAddressCompare" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const enable = conditionFormChecker({ props, data, executeKey: "enable", params: { sectionKey: "CompanyAddressCompare" } });
                    const sectionCompanyName1 = createFieldCompanyName({
                        props: { ...props, viewOnly: !enable },
                        data,
                        key: "attribute",
                        isLoading
                    });
                    const sectionCompanyName2 = createFieldCompanyName({
                        props: { ...props, viewOnly: !enable },
                        data,
                        key: "refCompanyTmp",
                        isLoading
                    });
                    const sectionCompanyAddress1 = createFieldCompanyAddress({
                        props: { ...props, viewOnly: !enable },
                        data,
                        key: "attribute",
                        isLoading
                    });
                    const sectionCompanyAddress2 = createFieldCompanyAddress({
                        props: { ...props, viewOnly: !enable },
                        data,
                        key: "refCompanyTmp",
                        isLoading
                    });
                    return (
                        <Row>
                            <Col xs={24} lg={12}>
                                <CardBorder title={i18n.global.t("views.company.address.title")}>
                                    <TemplateBuilder
                                        rowProp={data.rowProp}
                                        useForm={data.useForm}
                                        fields={[...sectionCompanyName1, ...sectionCompanyAddress1]}
                                        skeleton={isLoading.value}
                                    ></TemplateBuilder>
                                </CardBorder>
                            </Col>
                            <Col xs={24} lg={12}>
                                <CardBorder title={i18n.global.t("views.company.companyRequest.address.title")}>
                                    <TemplateBuilder
                                        rowProp={data.rowProp}
                                        useForm={data.useForm}
                                        fields={[...sectionCompanyName2, ...sectionCompanyAddress2]}
                                        skeleton={isLoading.value}
                                    ></TemplateBuilder>
                                </CardBorder>
                            </Col>
                        </Row>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "CompanyUser" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const enable = conditionFormChecker({ props, data, executeKey: "enable", params: { sectionKey: "CompanyUser" } });
                    const sectionCompanyUser = createFieldUser({ props: { ...props, viewOnly: !enable }, data, key: "attribute.refUser", isLoading });
                    return (
                        <CardBorder title={i18n.global.t("model.userCompany.refUser")}>
                            <TemplateBuilder
                                rowProp={data.rowProp}
                                useForm={data.useForm}
                                fields={sectionCompanyUser}
                                skeleton={isLoading.value}
                            ></TemplateBuilder>
                        </CardBorder>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "CompanyUserCompare" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const enable = conditionFormChecker({ props, data, executeKey: "enable", params: { sectionKey: "CompanyUserCompare" } });
                    const sectionCompanyAddress1 = createFieldUser({
                        props: { ...props, viewOnly: !enable },
                        data,
                        key: "attribute.refUserTmp",
                        isLoading
                    });
                    const sectionCompanyAddress2 = createFieldUser({
                        props: { ...props, viewOnly: !enable },
                        data,
                        key: "attribute.refUser",
                        isLoading
                    });
                    return (
                        <Row>
                            <Col xs={24} lg={12}>
                                <CardBorder title={i18n.global.t("model.userCompany.refUser")}>
                                    <TemplateBuilder
                                        rowProp={data.rowProp}
                                        useForm={data.useForm}
                                        fields={sectionCompanyAddress1}
                                        skeleton={isLoading.value}
                                    ></TemplateBuilder>
                                </CardBorder>
                            </Col>
                            <Col xs={24} lg={12}>
                                <CardBorder title={i18n.global.t("views.company.companyRequest.user.title")}>
                                    <TemplateBuilder
                                        rowProp={data.rowProp}
                                        useForm={data.useForm}
                                        fields={sectionCompanyAddress2}
                                        skeleton={isLoading.value}
                                    ></TemplateBuilder>
                                </CardBorder>
                            </Col>
                        </Row>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "Attachment" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const cardProp = lodash.merge(
                        {
                            title: i18n.global.t("views.company.companyRequest.attachment.title")
                        },
                        conditionFormChecker({ props, data, executeKey: "cardProp", params: { sectionKey: "Attachment" } })
                    );
                    const componentProp = lodash.mergeWith(
                        {
                            menuContext,
                            viewOnly: viewOnly,
                            parent: model
                        },
                        conditionFormChecker({ props, data, executeKey: "componentProp", params: { sectionKey: "Attachment" } }),
                        (objValue, srcValue, key) => {
                            if (["menuContext"].includes(key)) return srcValue || objValue;
                        }
                    );
                    return (
                        <CardBorder {...cardProp}>
                            <AttachmentList {...componentProp}></AttachmentList>
                        </CardBorder>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "CompanyInspection" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    return (
                        <CardBorder
                            title={i18n.global.t("views.company.companyRequest.privilege.companyInspection.title")}
                            collapsed={data.collapsed["CompanyInspection"]}
                            onUpdate:collapsed={collapsed => (data.collapsed["CompanyInspection"] = collapsed)}
                        >
                            <CompanyInspectionList
                                menuContext={menuContext}
                                viewOnly={true}
                                dataProp={data}
                                itemsKey="refCompanyTmp.companyInspections"
                                visibleCompany={false}
                                visibleSearch={false}
                            ></CompanyInspectionList>
                        </CardBorder>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "SamplingProduct" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const flatSamplingProducts = model.refCompanyTmp.samplingRequests.flatMap(d => d.samplingProducts);
                    const componentProp = lodash.mergeWith(
                        {
                            menuContext,
                            viewOnly: viewOnly,
                            dataProp: { ...data, useForm: { ...useForm, modelRef: { ...modelRef, flatSamplingProducts } } },
                            itemsKey: "flatSamplingProducts",
                            visibleTestReport: false
                        },
                        conditionFormChecker({ props, data, executeKey: "componentProp", params: { sectionKey: "SamplingProduct" } }),
                        (objValue, srcValue, key) => {
                            if (["menuContext"].includes(key)) return srcValue || objValue;
                        }
                    );
                    return (
                        <CardBorder
                            title={i18n.global.t("views.company.companyRequest.privilege.samplingProduct.title")}
                            collapsed={data.collapsed["SamplingProduct"]}
                            onUpdate:collapsed={collapsed => (data.collapsed["SamplingProduct"] = collapsed)}
                        >
                            <SamplingProductList {...componentProp}></SamplingProductList>
                        </CardBorder>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "CompanyCert" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const flatCertificateProducts = model.refCompanyTmp.certificateRequests.flatMap(d => d.certificateProducts);
                    const componentProp = lodash.mergeWith(
                        {
                            menuContext,
                            viewOnly: viewOnly,
                            dataProp: { ...data, useForm: { ...useForm, modelRef: { ...modelRef, flatCertificateProducts } } },
                            itemsKey: "flatCertificateProducts"
                        },
                        conditionFormChecker({ props, data, executeKey: "componentProp", params: { sectionKey: "CompanyCert" } }),
                        (objValue, srcValue, key) => {
                            if (["menuContext"].includes(key)) return srcValue || objValue;
                        }
                    );
                    return (
                        <CardBorder
                            title={i18n.global.t("views.company.companyRequest.privilege.companyCert.title")}
                            collapsed={data.collapsed["CompanyCert"]}
                            onUpdate:collapsed={collapsed => (data.collapsed["CompanyCert"] = collapsed)}
                        >
                            <HealthCertList {...componentProp}></HealthCertList>
                        </CardBorder>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "Receive" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const sectionReceive = createFieldReceive({ props, data, isLoading });
                    return (
                        <CardBorder title={i18n.global.t("views.company.companyRequest.receive.title")}>
                            <TemplateBuilder
                                rowProp={data.rowProp}
                                useForm={data.useForm}
                                fields={sectionReceive}
                                skeleton={isLoading.value}
                            ></TemplateBuilder>
                        </CardBorder>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "Inspection" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const sectionReview = createFieldCompanyInspection({ props, data, key: "refCompanyTmp.companyInspections.0", isLoading });
                    sectionReview.push(createFieldCompanyThNo({ props, data, key: "refCompanyTmp", isLoading }));
                    const companyInspection = model.refCompanyTmp.companyInspections[0];
                    companyInspection.isSave = true;
                    return (
                        <CardBorder title={i18n.global.t("views.company.companyRequest.review.title")}>
                            <TemplateBuilder
                                rowProp={data.rowProp}
                                useForm={data.useForm}
                                fields={sectionReview}
                                skeleton={isLoading.value}
                            ></TemplateBuilder>
                        </CardBorder>
                    );
                }
            }
        ]
    });
};

export const createFieldCompanyThNo = ({ props, data, key }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return createFieldInput({
        visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
        key: `${prefix}thNo`,
        colProp: () => ({ xs: 24, md: 12 }),
        formItemProp: () => ({ labelCol: { xs: 24, md: 9 }, wrapperCol: { xs: 24, md: 15 } }),
        label: () => i18n.global.t("model.company.thNo"),
        inputProp: ({ field }) =>
            lodash.merge(
                {
                    value: model.thNo,
                    "onUpdate:value": value => (model.thNo = value),
                    disabled: viewOnly
                },
                conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
            )
    });
};

export const createFieldCompanyRequest = ({ props, data, key }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refCompany`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.companyRequest.refCompany"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refCompany,
                            "onUpdate:value": value => (model.refCompany = value),
                            disabled: viewOnly,
                            list: ({ searchQuery }) => callUserCompanyByNameTh({ searchQuery }).then(items => items.map(d => d.refCompany)),
                            optLabel: "nameTh"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.nameTh`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refCompanyRequestType`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.companyRequest.refCompanyRequestType"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refCompanyRequestType,
                            "onUpdate:value": value => (model.refCompanyRequestType = value),
                            disabled: viewOnly,
                            list: ({ searchQuery }) => callCompanyRequestType({ searchQuery, company: model.refCompany, props }),
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            })
        ]
    });
};

export const createFieldReceive = ({ props, data, key }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refCreateBy.fullName`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.companyRequest.refCreateBy"),
                inputProp: () => ({
                    value: model.refCreateBy?.fullName,
                    disabled: true
                })
            }),
            createFieldDatetime({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}receiveDatetime`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.companyRequest.receiveDatetime"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.receiveDatetime,
                            "onUpdate:value": value => (model.receiveDatetime = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refReceiver.fullName`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.companyRequest.refReceiver"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refReceiver?.fullName,
                            "onUpdate:value": value => (model.refReceiver.fullName = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldRadioGroup({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refStatus`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.companyRequest.refStatus"),
                inputProp: ({ field }) =>
                    lodash.mergeWith(
                        {
                            value: model.refStatus,
                            "onUpdate:value": value => (model.refStatus = value),
                            disabled: viewOnly,
                            list: callCompanyRequestStatusForReceive,
                            optLabel: "attribute.actionText"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } }),
                        (objValue, srcValue, key) => {
                            if (["value"].includes(key)) return srcValue || objValue;
                        }
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}remark`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.companyRequest.remark"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.remark,
                            "onUpdate:value": value => (model.remark = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            })
        ]
    });
};

export const onClickOkCompanyRequest = ({ data, key }) => {
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    useForm
        .validate(["refCompany", "refCompanyRequestType"])
        .then(() => {
            setIsLoadingApp(true);
            Promise.all([callCompanyById({ id: model.refCompany.id }), callCloneCompany({ data: model })])
                .finally(() => setIsLoadingApp(false))
                .then(arr => {
                    model.refCompany = arr[0];
                    model.refCompanyTmp = arr[1].refCompanyTmp;
                    model.attribute = arr[1].attribute;
                    if (["7"].includes(model.refCompanyRequestType?.key)) {
                        data.collapsed["CompanyInspection"] = true;
                        data.collapsed["SamplingProduct"] = true;
                        data.collapsed["CompanyCert"] = true;
                        model.refCompanyTmp.companyProducts.forEach(d => {
                            if (d.refStatus?.key === "Approve") d.isCheckedPrivilege = true;
                        });
                    }
                });
        })
        .catch(() => {});
};

export const onClickEditCompanyRequest = ({ data, key }) => {
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    Promise.resolve()
        .then(() => clearRulesStartsWith({ useForm, key: `${prefix}refCompanyTmp` }))
        .then(() => (model.refCompanyTmp = null));
};

export const createRulesCompanyProduct = ({ data, key }) => {
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    clearRulesStartsWith({ useForm, key: `${prefix}companyProducts` });
    //addRules({ useForm, modelRef, key: `${prefix}companyProducts`, rules: [required], defaultValue: [] })
    model?.companyProducts?.forEach((/* companyProduct, index */) => {
        //addRules({ useForm, modelRef, key: `${prefix}companyProducts.${index}.refStatus`, rules: [required] })
        /*
        addRules({
            useForm, modelRef, key: `${prefix}companyProducts.${index}.refStatus`, rules: [{
                validator: (rule, value) => {
                    if (data.sendApprove) {
                        if (!['Approve', 'Reject'].includes(value?.key)) {
                            return Promise.reject(i18n.global.t("error.companyRequest.sendApprove.product.status"))
                        }
                    }
                    return Promise.resolve()
                },
                trigger: "change"
            }]
        })
        */
    });
};

export const createRulesCompanyCountry = ({ data, key }) => {
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    clearRulesStartsWith({ useForm, key: `${prefix}companyCountries` });
    //addRules({ useForm, modelRef, key: `${prefix}companyCountries`, rules: [maxLength(2)], defaultValue: [] })
    model?.companyCountries?.forEach((companyCountry, index) => {
        addRules({
            useForm,
            modelRef,
            key: `${prefix}companyCountries.${index}.refAttach`,
            rules: [requiredWhen({ when: () => companyCountry.refCountry.refAttach })]
        });
        //addRules({ useForm, modelRef, key: `${prefix}companyCountries.${index}.refStatus`, rules: [required] })
        /*
        addRules({
            useForm, modelRef, key: `${prefix}companyCountries.${index}.refStatus`, rules: [{
                validator: (rule, value) => {
                    if (data.sendApprove) {
                        if (!['Approve', 'Reject'].includes(value?.key)) {
                            return Promise.reject(i18n.global.t("error.companyRequest.sendApprove.country.status"))
                        }
                    }
                    return Promise.resolve()
                },
                trigger: "change"
            }]
        })
        */
    });
};

export const createRulesCompanyInspection = ({ data, key }) => {
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    clearRulesStartsWith({ useForm, key: `${prefix}companyInspections` });
    model?.companyInspections?.forEach((companyInspection, index) => {
        addRules({
            useForm,
            modelRef,
            key: `${prefix}companyInspections.${index}.inspectionStartDatetime`,
            rules: [requiredWhen({ when: () => data.sendApprove || data.sendConfirm })]
        });
        addRules({
            useForm,
            modelRef,
            key: `${prefix}companyInspections.${index}.inspectionEndDatetime`,
            rules: [requiredWhen({ when: () => data.sendApprove || data.sendConfirm })]
        });
        addRules({
            useForm,
            modelRef,
            key: `${prefix}companyInspections.${index}.refInspectionType`,
            rules: [requiredWhen({ when: () => data.sendApprove || data.sendConfirm })]
        });
        addRules({
            useForm,
            modelRef,
            key: `${prefix}companyInspections.${index}.refInspector`,
            rules: [requiredWhen({ when: () => data.sendApprove || data.sendConfirm })]
        });
        /*
        addRules({
            useForm,
            modelRef,
            key: `${prefix}companyInspections.${index}.refGmpLv`,
            rules: [requiredWhen({ when: () => data.sendApprove || data.sendConfirm })]
        });
        */
        addRules({
            useForm,
            modelRef,
            key: `${prefix}companyInspections.${index}.refGmpStatus`,
            rules: [requiredWhen({ when: () => data.sendApprove || data.sendConfirm })]
        });
        addRules({
            useForm,
            modelRef,
            key: `${prefix}companyInspections.${index}.refHaccpStatus`,
            rules: [requiredWhen({ when: () => data.sendApprove || data.sendConfirm })]
        });
        addRules({
            useForm,
            modelRef,
            key: `${prefix}companyInspections.${index}.c`,
            rules: [requiredWhen({ when: () => data.sendApprove || data.sendConfirm })]
        });
        addRules({
            useForm,
            modelRef,
            key: `${prefix}companyInspections.${index}.se`,
            rules: [requiredWhen({ when: () => data.sendApprove || data.sendConfirm })]
        });
        addRules({
            useForm,
            modelRef,
            key: `${prefix}companyInspections.${index}.m`,
            rules: [requiredWhen({ when: () => data.sendApprove || data.sendConfirm })]
        });
        addRules({
            useForm,
            modelRef,
            key: `${prefix}companyInspections.${index}.n`,
            rules: [requiredWhen({ when: () => data.sendApprove || data.sendConfirm })]
        });
    });
};

export const createRulesCompanyUser = ({ data, key }) => {
    const { useForm } = data;
    const { modelRef } = useForm;
    const prefix = key ? `${key}.` : "";
    addRules({
        useForm,
        modelRef,
        key: `${prefix}refUser.idCard`,
        rules: [required, equalsLength(13), idCardRequired()],
        isSet: false
    });
    addRules({ useForm, modelRef, key: `${prefix}refUser.email`, rules: [required, email, emailRequired()], isSet: false });
    addRules({ useForm, modelRef, key: `${prefix}refUser.refTitle`, rules: [required], isSet: false });
    addRules({ useForm, modelRef, key: `${prefix}refUser.firstNameTh`, rules: [required], isSet: false });
    addRules({ useForm, modelRef, key: `${prefix}refUser.lastNameTh`, rules: [required], isSet: false });
    addRules({ useForm, modelRef, key: `${prefix}refUser.phone`, rules: [required], isSet: false });
};
